<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { LoadingView } from '@hfc/ui-core';
import { CalendlyEventResponse, CalendlyEvent } from '@/consent/medicare/domain/CalendlyEventResponse';

const props = defineProps<{
  appointmentLink: string
  loading?: boolean
  prefill?: unknown
}>();

interface emitInterface {
  (event: 'eventScheduled', data: CalendlyEvent): void
  (event: 'dateAndTimeSelected', data: CalendlyEvent): void
}

const emits = defineEmits<emitInterface>();

const iframeLoaded = ref(false);
const showLoading = computed(() => !iframeLoaded.value || !!props.loading);

const isCalendlyEvent = (e: MessageEvent):boolean => e.data.event && e.data.event.indexOf('calendly') === 0;

const handleCalendlyMessage = async (e: MessageEvent<CalendlyEventResponse>):Promise<void> => {
  if (!isCalendlyEvent(e)) {
    return;
  }

  if (e.data.event === 'calendly.date_and_time_selected') {
    emits('dateAndTimeSelected', getEventData(e.data));

    return;
  }

  if (e.data.event === 'calendly.event_scheduled') {
    emits('eventScheduled', getEventData(e.data));

    return;
  }
};

const getEventData = (response: CalendlyEventResponse): CalendlyEvent => {
  const appointment_id = getIdFromUrl(response.payload.event?.uri ?? '-');
  const invitee_id = getIdFromUrl(response.payload.invitee?.uri ?? '-');

  return { appointment_id, invitee_id };
};

const getIdFromUrl = (url:string): string => {
  return url.split('/').pop() ?? '';
};

onMounted(async () => {
  window.Calendly.initInlineWidget({
    url: props.appointmentLink,
    parentElement: document.getElementById('hfc-appointment-calendly-container'),
    prefill: props.prefill,
  });
  waitForIframeToLoad();

  window.addEventListener('message', handleCalendlyMessage);
});

const waitForIframeToLoad = (): void => {
  const container = document.getElementById('hfc-appointment-calendly-container');

  const checkIframeLoaded = (): void => {
    const iframe = container?.querySelector<HTMLIFrameElement>('iframe');
    if (iframe) {
      iframe.onload = (): void => {
        iframeLoaded.value = true;
        clearInterval(intervalId);
      };
    }
  };

  const intervalId = setInterval(checkIframeLoaded, 100);
};

onBeforeUnmount(() => {
  window.removeEventListener('message', handleCalendlyMessage);
});

</script>
<template>
  <div class="flex flex-1 relative p-[0!important] min-h-[calc(100vh-350px)] overflow-hidden">
    <div v-if="showLoading" class="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-blue-100/30 backdrop-blur-md">
      <LoadingView class="bg-transparent" />
    </div>
    <div id="hfc-appointment-calendly-container" class="absolute w-full rounded-lg h-[calc(100%+50px)] border-none md:mt-[-50px]"></div>
  </div>
</template>
